import { useState, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import {
  Page,
  PageTitleWithButton,
  Table,
  Pagination,
  Loader,
  MobileTable,
  SearchSection
} from '../../../components';
import { useQuery } from 'react-query';
import { Box, Button, useMediaQuery } from '@mui/material';
import { Edit, Add, DeleteForever } from '@mui/icons-material';
import { formatInputPhoneNumber } from '../../../helpers';
import { deleteForemanUser } from '../../../fetch';
import { IResponse } from '../../../models';
import { ForemenModal } from './foremen-modal';
import { ForemenPageFilters } from './foremen-page-filters';
import { getForemanUsers } from '../../../fetch/foremen';
import { IForemanUser } from '../../../models/foremen';

export const ForemenPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoadingForemanUser, setLoadingForemanUser] = useState<boolean>(false);
  const [isModalOpen, showForemanUserModal] = useState<boolean>(false);
  const [currentForemanUserId, setCurrentForemanUserId] = useState<string | number>(undefined);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [selectedSort, setSelectedSort] = useState<string>('Name');
  const [sortDirection, setSortDirection] = useState<{
    Name?: 'Asc' | 'Desc';
    DisplayName?: 'Asc' | 'Desc';
    ContactPhoneNumber?: 'Asc' | 'Desc';
    ActiveBuildCount?: 'Asc' | 'Desc';
  }>({
    Name: 'Asc'
  });
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const [filters, setFilters] = useState<{
    search?: string;
    neighborhoodId?: string | number;
    territoryId?: string | number;
  } | null>({});
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<string | number>('');
  const [selectedTerritoryId, setSelectedTerritoryId] = useState<string | number>('');
  const [searchedValue, setSearchedValue] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');

  const isMobile = useMediaQuery('(max-width: 960px)');

  const {
    isLoading: isLoadingForemanUsers,
    data: foremanUsersData,
    refetch: refetchForemanUsers
  } = useQuery<IResponse<IForemanUser[]>, Error>(
    ['foremanUsers', page, perPage, selectedSort, sortDirection, filters, searchedValue],
    () =>
      getForemanUsers({
        page: page + 1,
        perPage,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        searchTerm: searchedValue || filters?.search,
        territoryId: filters?.territoryId,
        neighborhoodId: filters?.neighborhoodId
      }),
    {
      onSuccess: data => {
        setRecordCount(data.totalRecordCount);
      },
      notifyOnChangeProps: 'tracked'
    }
  );
  const foremanUsers = useMemo(() => foremanUsersData?.records ?? [], [foremanUsersData]);

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const handleEdit = async (id: string | number) => {
    setCurrentForemanUserId(id);
    showForemanUserModal(true);
  };
  const handleDelete = async (id: string | number) => {
    const result = window.confirm('Are you sure you want to delete this foreman user?');

    if (result) {
      setLoadingForemanUser(true);
      try {
        await deleteForemanUser(id);
        refetchForemanUsers();
        enqueueSnackbar('Foreman user removed!', {
          variant: 'success'
        });
      } catch (error) {
        enqueueSnackbar(error?.Detail ?? `Error deleting foreman user, please try again.`, {
          variant: 'error'
        });
      } finally {
        setLoadingForemanUser(false);
      }
    }
  };
  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        isServerSorted: selectedSort === 'Name',
        isServerSortedDesc: sortDirection.Name === 'Desc',
        handleClickColumn: () => handleClickColumn('Name')
      },
      {
        Header: 'Display Name',
        accessor: 'displayName',
        isServerSorted: selectedSort === 'DisplayName',
        isServerSortedDesc: sortDirection.DisplayName === 'Desc',
        handleClickColumn: () => handleClickColumn('DisplayName')
      },
      {
        Header: 'Territories',
        accessor: 'territories',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IForemanUser } };
        }) => {
          return <>{original.territories.join(', ')}</>;
        }
      },
      {
        Header: 'Neighborhoods',
        accessor: 'neighborhoods',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IForemanUser } };
        }) => {
          return <>{original.neighborhoods.join(', ')}</>;
        }
      },
      {
        Header: 'Contact Number',
        accessor: 'contactPhoneNumber',
        isServerSorted: selectedSort === 'ContactPhoneNumber',
        isServerSortedDesc: sortDirection.ContactPhoneNumber === 'Desc',
        handleClickColumn: () => handleClickColumn('ContactPhoneNumber'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IForemanUser } };
        }) => {
          return <>{formatInputPhoneNumber(original.contactPhoneNumber)}</>;
        }
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        overrideWidth: 200,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IForemanUser } };
        }) => {
          return (
            <Box display="flex">
              <Button
                id="edit"
                color="primary"
                startIcon={<Edit />}
                onClick={async () => {
                  handleEdit(original.foremanUserId);
                }}
              >
                Edit
              </Button>
              <Button
                id="delete"
                color="error"
                startIcon={<DeleteForever />}
                onClick={async () => {
                  console.log(original);
                  handleDelete(original.foremanUserId);
                }}
              >
                Delete
              </Button>
            </Box>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foremanUsers]);
  return (
    <>
      {isLoadingForemanUser && (
        <Loader position="centered" type="fullscreen">
          Loading...
        </Loader>
      )}
      <Page title="Foremen">
        <PageTitleWithButton
          pageTitleProps={{
            title: 'Foremen',
            showDivider: false
          }}
          button={
            <Button
              startIcon={<Add />}
              onClick={() => showForemanUserModal(true)}
              color="primary"
              variant="contained"
            >
              Add New Foreman
            </Button>
          }
        />
        <Box mt={1}>
          <ForemenPageFilters
            isLoading={isLoadingForemanUsers}
            setHasAppliedFilters={setHasAppliedFilters}
            hasAppliedFilters={hasAppliedFilters}
            setSearchValue={setSearchValue}
            setSearchedValue={setSearchedValue}
            searchValue={searchValue}
            setSelectedNeighborhoodId={setSelectedNeighborhoodId}
            selectedNeighborhoodId={selectedNeighborhoodId}
            setSelectedTerritoryId={setSelectedTerritoryId}
            selectedTerritoryId={selectedTerritoryId}
            handleSearch={(val: string) => {
              setPage(0);
              setSearchedValue(val);
            }}
            applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
              if (clearFilters) {
                setFilters({
                  search: '',
                  neighborhoodId: undefined,
                  territoryId: undefined
                });
              } else {
                setPage(0);
                setFilters({
                  ...filters,
                  search: searchValue,
                  neighborhoodId: selectedNeighborhoodId || undefined,
                  territoryId: selectedTerritoryId || undefined
                });
              }
            }}
          />
          {searchedValue && (
            <SearchSection
              searchedValue={searchedValue}
              handleClearSearch={() => {
                setSearchValue('');
                setSearchedValue('');
                setHasAppliedFilters(false);
                setPage(0);
              }}
            />
          )}
        </Box>
        <Table
          columns={columns}
          data={foremanUsers}
          isLoading={isLoadingForemanUsers}
          hidePagination
          mobileProps={{
            handleEdit: (val: IForemanUser) => {
              handleEdit(val.foremanUserId);
            },
            handleDelete: (val: IForemanUser) => {
              handleDelete(val.foremanUserId);
            },
            fields: columns
              .filter(c => c.Header)
              .map(c => ({
                name: c.Header,
                accessor: c.accessor,
                Cell: c?.Cell
              }))
          }}
          ResponsiveComponent={isMobile ? MobileTable : undefined}
        />
        {!isLoadingForemanUsers && (
          <Pagination
            page={page}
            count={recordCount}
            rowsPerPage={perPage}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
          />
        )}
        <ForemenModal
          foremanUserId={currentForemanUserId}
          open={isModalOpen}
          onClose={() => {
            showForemanUserModal(false);
            setTimeout(() => {
              setCurrentForemanUserId(undefined);
            }, 500);
          }}
          fetchForemanUsers={refetchForemanUsers}
        />
      </Page>
    </>
  );
};
